import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Gauge, AlertTriangle, CheckCircle2, TimerReset, Clock,
  Bell, ExternalLink, RefreshCw, Loader2, Play, VolumeX, X,
} from "lucide-react";
import { API } from "../../config";
import { useAuth } from "../../context/AuthContext";

// ── Small helpers ───────────────────────────────────────────────────

const fmtAgo = (minutes) => {
  if (minutes === null || minutes === undefined) return "mai";
  if (minutes < 1) return "adesso";
  if (minutes < 60) return `${minutes} min fa`;
  const h = Math.floor(minutes / 60);
  if (h < 24) return `${h} h fa`;
  const d = Math.floor(h / 24);
  return `${d} g fa`;
};

const overallLevel = (data) => {
  if (!data) return "unknown";
  if (data.breach_count > 0) return "breach";
  if (data.over_alert_threshold > 0) return "alert";
  if (data.warn_count > 0) return "warn";
  if (data.active_total === 0) return "empty";
  return "ok";
};

const LEVEL_STYLE = {
  breach: {
    ring: "border-red-500/60",
    bg: "bg-gradient-to-br from-red-950/50 via-[#111] to-[#111]",
    dot: "bg-red-500",
    text: "text-red-300",
    label: "SLA sforata in corso",
    icon: AlertTriangle,
  },
  alert: {
    ring: "border-red-500/60",
    bg: "bg-gradient-to-br from-red-950/40 via-[#111] to-[#111]",
    dot: "bg-red-500",
    text: "text-red-300",
    label: "Richieste sopra soglia allerta",
    icon: AlertTriangle,
  },
  warn: {
    ring: "border-amber-500/60",
    bg: "bg-gradient-to-br from-amber-950/40 via-[#111] to-[#111]",
    dot: "bg-amber-500",
    text: "text-amber-300",
    label: "In attenzione",
    icon: TimerReset,
  },
  ok: {
    ring: "border-emerald-700/60",
    bg: "bg-gradient-to-br from-emerald-950/30 via-[#111] to-[#111]",
    dot: "bg-emerald-500",
    text: "text-emerald-300",
    label: "Nei tempi",
    icon: CheckCircle2,
  },
  empty: {
    ring: "border-gray-800",
    bg: "bg-[#111]",
    dot: "bg-gray-600",
    text: "text-gray-500",
    label: "Nessuna richiesta pendente",
    icon: CheckCircle2,
  },
  unknown: {
    ring: "border-gray-800",
    bg: "bg-[#111]",
    dot: "bg-gray-600",
    text: "text-gray-500",
    label: "Sconosciuto",
    icon: Gauge,
  },
};

// ── Widget ──────────────────────────────────────────────────────────

/**
 * SLA Health widget for the Admin Dashboard (Zorzutti CR 03/08/2026).
 *
 * Bridges admin-view and action:
 *  1. At-a-glance status colour: green (ok) → amber (warn) → red (breach)
 *  2. Live counters mirroring the dealer portal SLA-guard bar
 *  3. Timestamp of the last watchdog dispatch (freshness indicator)
 *  4. CTA "Apri portale dealer" so Fabrizio can approve/reject directly
 *  5. Manual "Force run" button for on-demand watchdog dispatch
 */
export const SlaHealthWidget = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forcing, setForcing] = useState(false);

  // Quiet-baseline modal state — a two-step confirmation flow so
  // Fabrizio can't accidentally silence the entire pending backlog.
  // ``qbStep`` progression:
  //   null → not open
  //   "preview" → GET /preview done, showing count + list, waiting for user click
  //   "confirm" → user clicked "Prosegui" — now must type "SILENZIA" to arm the button
  //   "applying" → POST /apply in-flight
  const [qbOpen, setQbOpen] = useState(false);
  const [qbStep, setQbStep] = useState(null);
  const [qbPreview, setQbPreview] = useState(null);
  const [qbTyped, setQbTyped] = useState("");
  const [qbApplying, setQbApplying] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/admin/system/change-requests/sla-status`);
      setData(r.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Refresh every 60s — the underlying counts change at most every
    // few minutes (dealer approves/rejects, customer submits), so a
    // longer poll interval avoids hammering Mongo for open tabs.
    const iv = setInterval(fetchData, 60_000);
    return () => clearInterval(iv);
  }, [fetchData]);

  const runWatchdogNow = async () => {
    if (forcing) return;
    setForcing(true);
    try {
      const r = await axios.post(
        `${API}/admin/system/change-requests/sla-watchdog/run-now`
      );
      const rep = r.data || {};
      if (rep.alerted > 0) {
        toast.success(
          `Watchdog: ${rep.alerted} alert inviat${rep.alerted === 1 ? "o" : "i"} a ${rep.recipients?.length || 0} admin`
        );
      } else if (rep.reason === "no_breach") {
        toast.info("Watchdog: nessuna richiesta sopra soglia — niente da segnalare");
      } else {
        toast.info(`Watchdog eseguito · esito: ${rep.reason || "sconosciuto"}`);
      }
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'esecuzione watchdog");
    } finally {
      setForcing(false);
    }
  };

  // ── Quiet Baseline flow (super_admin only) ──────────────────────
  const openQuietBaseline = async () => {
    setQbOpen(true);
    setQbStep("preview");
    setQbPreview(null);
    setQbTyped("");
    try {
      const r = await axios.get(
        `${API}/admin/system/change-requests/quiet-baseline/preview`
      );
      setQbPreview(r.data);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Errore nel caricare l'anteprima"
      );
      setQbOpen(false);
    }
  };

  const closeQuietBaseline = () => {
    if (qbApplying) return; // don't let the user close during the network call
    setQbOpen(false);
    setQbStep(null);
    setQbPreview(null);
    setQbTyped("");
  };

  const applyQuietBaseline = async () => {
    if (qbApplying) return;
    setQbApplying(true);
    try {
      const r = await axios.post(
        `${API}/admin/system/change-requests/quiet-baseline/apply`
      );
      const flagged = r.data?.flagged ?? 0;
      toast.success(
        flagged === 0
          ? "Nessuna richiesta da silenziare — coda già pulita."
          : `${flagged} richiest${flagged === 1 ? "a silenziata" : "e silenziate"}. Il watchdog non le segnalerà più.`
      );
      await fetchData();
      closeQuietBaseline();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Errore nell'applicare la baseline"
      );
    } finally {
      setQbApplying(false);
    }
  };

  const level = overallLevel(data);
  const style = LEVEL_STYLE[level];
  const Icon = style.icon;

  return (
    <div
      className={`rounded-xl border ${style.ring} ${style.bg} p-5`}
      data-testid="sla-health-widget"
      data-sla-level={level}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${style.bg} border ${style.ring}`}>
            <Icon size={20} className={style.text} />
          </div>
          <div>
            <h3 className="text-white font-semibold text-base flex items-center gap-2">
              <span className={`inline-block w-2 h-2 rounded-full ${style.dot}`}></span>
              SLA Health · Change Requests
            </h3>
            <p className={`text-xs ${style.text} mt-0.5`}>
              {style.label}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchData}
            disabled={loading}
            className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition"
            title="Aggiorna"
            data-testid="sla-health-refresh"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 size={24} className="text-gray-500 animate-spin" />
        </div>
      ) : !data ? (
        <p className="text-gray-500 text-sm">Impossibile caricare lo stato SLA.</p>
      ) : (
        <>
          {/* One-line human summary */}
          <p
            className="text-gray-300 text-sm leading-relaxed mb-4"
            data-testid="sla-health-summary"
          >
            {data.active_total === 0 ? (
              <>Nessuna richiesta di correzione pre-firma in attesa.</>
            ) : (
              <>
                <strong className="text-white">{data.active_total}</strong>{" "}
                {data.active_total === 1 ? "richiesta attiva" : "richieste attive"}
                {data.breach_count > 0 && (
                  <>
                    ,{" "}
                    <strong className="text-red-400" data-testid="sla-health-breach-inline">
                      {data.breach_count}{" "}
                      {data.breach_count === 1 ? "ha sforato" : "hanno sforato"}
                      {" "}le {data.thresholds.breach_hours}h
                    </strong>
                  </>
                )}
                {data.breach_count === 0 && data.over_alert_threshold > 0 && (
                  <>
                    ,{" "}
                    <strong className="text-red-300">
                      {data.over_alert_threshold} sopra le {data.thresholds.alert_hours}h
                    </strong>{" "}
                    (soglia allerta)
                  </>
                )}
                {data.breach_count === 0 &&
                  data.over_alert_threshold === 0 &&
                  data.warn_count > 0 && (
                    <>
                      ,{" "}
                      <strong className="text-amber-300">
                        {data.warn_count} in attenzione (&gt;{data.thresholds.warn_hours}h)
                      </strong>
                    </>
                  )}
                . Ultima allerta:{" "}
                <span className="text-gray-400" data-testid="sla-health-last-alert">
                  {fmtAgo(data.last_alert_minutes_ago)}
                </span>
                .
              </>
            )}
          </p>

          {/* Counter chips */}
          <div className="flex items-center gap-2 flex-wrap mb-4">
            {data.breach_count > 0 && (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-600/20 border border-red-500 text-red-100 text-[11px] font-bold uppercase tracking-wider animate-pulse"
                data-testid="sla-health-chip-breach"
              >
                <AlertTriangle size={12} />
                {data.breach_count} sforate
              </span>
            )}
            {data.warn_count > 0 && (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-600/20 border border-amber-500 text-amber-100 text-[11px] font-bold uppercase tracking-wider"
                data-testid="sla-health-chip-warn"
              >
                <TimerReset size={12} />
                {data.warn_count} in attenzione
              </span>
            )}
            {data.ok_count > 0 && (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-900/30 border border-emerald-700/60 text-emerald-200 text-[11px] font-semibold uppercase tracking-wider"
                data-testid="sla-health-chip-ok"
              >
                <Clock size={12} />
                {data.ok_count} nei tempi
              </span>
            )}
            {data.active_total === 0 && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-800 border border-gray-700 text-gray-400 text-[11px] font-semibold uppercase tracking-wider">
                <CheckCircle2 size={12} />
                Coda vuota
              </span>
            )}
          </div>

          {/* Action row */}
          <div className="flex items-center gap-2 flex-wrap pt-3 border-t border-gray-800/60">
            <a
              href="/dealer?redirect=change_requests"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-lojack-red hover:bg-red-700 text-white text-xs font-medium rounded-lg transition"
              data-testid="sla-health-open-portal"
            >
              <ExternalLink size={13} />
              Apri portale dealer
            </a>
            <button
              onClick={runWatchdogNow}
              disabled={forcing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-200 text-xs font-medium rounded-lg transition disabled:opacity-50"
              data-testid="sla-health-force-run"
            >
              {forcing ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Play size={13} />
              )}
              Esegui watchdog ora
            </button>
            {isSuperAdmin && (
              <button
                onClick={openQuietBaseline}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-200 text-xs font-medium rounded-lg transition"
                data-testid="sla-health-quiet-baseline"
                title="Silenzia le richieste storiche già oltre soglia (evita spam del watchdog)"
              >
                <VolumeX size={13} />
                Silenzia storico
              </button>
            )}
            <span className="text-gray-600 text-[10px] uppercase tracking-wider ml-auto flex items-center gap-1">
              <Bell size={10} />
              Soglia allerta {data.thresholds.alert_hours}h · promessa cliente {data.thresholds.breach_hours}h
            </span>
          </div>
        </>
      )}

      {/* ── Quiet-baseline confirmation modal ────────────────────
          Two-step guard: user must (1) see the count and click
          "Prosegui", (2) type "SILENZIA" to arm the apply button.
          Only rendered for super_admin. */}
      {qbOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          data-testid="sla-quiet-baseline-modal"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeQuietBaseline();
          }}
        >
          <div className="w-full max-w-lg bg-[#111] border border-gray-800 rounded-xl shadow-2xl">
            {/* Header */}
            <div className="flex items-start justify-between p-5 border-b border-gray-800">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-amber-900/40 border border-amber-700/60 flex items-center justify-center">
                  <VolumeX size={18} className="text-amber-300" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-base">
                    Silenzia storico SLA
                  </h3>
                  <p className="text-gray-500 text-xs mt-0.5">
                    Le richieste selezionate NON riceveranno più allerte dal watchdog.
                  </p>
                </div>
              </div>
              <button
                onClick={closeQuietBaseline}
                disabled={qbApplying}
                className="p-1 text-gray-500 hover:text-white transition disabled:opacity-40"
                data-testid="sla-quiet-baseline-close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="p-5">
              {qbPreview === null ? (
                <div className="py-8 flex items-center justify-center gap-2 text-gray-500">
                  <Loader2 size={16} className="animate-spin" />
                  Analisi in corso…
                </div>
              ) : qbPreview.count === 0 ? (
                <div className="py-6 text-center">
                  <CheckCircle2 size={32} className="text-emerald-400 mx-auto mb-2" />
                  <p className="text-white font-medium">Nessuna richiesta da silenziare</p>
                  <p className="text-gray-500 text-sm mt-1">
                    Nessuna richiesta pre-firma è oltre le {qbPreview.threshold_hours}h senza flag.
                  </p>
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <div
                      className="text-3xl font-bold text-amber-300 mb-1"
                      data-testid="sla-quiet-baseline-count"
                    >
                      {qbPreview.count}
                    </div>
                    <p className="text-gray-300 text-sm">
                      {qbPreview.count === 1 ? "richiesta" : "richieste"} pre-firma sopra le{" "}
                      <strong className="text-white">{qbPreview.threshold_hours}h</strong> senza flag di allerta.
                    </p>
                    <p className="text-gray-500 text-xs mt-2">
                      Cliccando &laquo;Silenzia&raquo; verranno marcate come &laquo;già alertate&raquo;, così il watchdog automatico non manderà email ai LoJack admin per queste richieste specifiche. Le nuove richieste che sforeranno le {qbPreview.threshold_hours}h da adesso in poi verranno segnalate normalmente.
                    </p>
                  </div>

                  {/* Preview list */}
                  {qbPreview.preview?.length > 0 && (
                    <div className="mb-4 max-h-52 overflow-y-auto rounded-lg border border-gray-800 divide-y divide-gray-800/60">
                      {qbPreview.preview.map((row) => (
                        <div
                          key={row.id}
                          className="flex items-center justify-between gap-3 px-3 py-2 text-xs"
                          data-testid={`sla-quiet-baseline-row-${row.id}`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="text-white font-medium truncate">
                              {row.customer_name || "—"}
                            </div>
                            <div className="text-gray-500 font-mono text-[10px] truncate">
                              {row.customer_email || "—"} · [{row.field}]
                            </div>
                          </div>
                          <div className="text-red-300 font-bold tabular-nums">
                            {row.age_hours ?? "—"}h
                          </div>
                        </div>
                      ))}
                      {qbPreview.count > qbPreview.preview.length && (
                        <div className="px-3 py-2 text-[11px] text-gray-500 text-center bg-[#0a0a0a]">
                          … +{qbPreview.count - qbPreview.preview.length} altre non mostrate
                        </div>
                      )}
                    </div>
                  )}

                  {/* Confirmation gate */}
                  {qbStep === "preview" ? (
                    <div className="flex items-center gap-2 justify-end pt-2 border-t border-gray-800/60">
                      <button
                        onClick={closeQuietBaseline}
                        className="px-4 py-2 text-gray-300 hover:text-white text-sm transition"
                        data-testid="sla-quiet-baseline-cancel"
                      >
                        Annulla
                      </button>
                      <button
                        onClick={() => setQbStep("confirm")}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition"
                        data-testid="sla-quiet-baseline-proceed"
                      >
                        Prosegui
                      </button>
                    </div>
                  ) : (
                    <div className="pt-4 border-t border-gray-800/60">
                      <label className="block text-gray-300 text-xs mb-2">
                        Per confermare, digita <strong className="text-white font-mono">SILENZIA</strong> qui sotto:
                      </label>
                      <input
                        type="text"
                        value={qbTyped}
                        onChange={(e) => setQbTyped(e.target.value.toUpperCase())}
                        placeholder="SILENZIA"
                        autoFocus
                        className="w-full bg-[#0a0a0a] border border-gray-800 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-amber-500 mb-3"
                        data-testid="sla-quiet-baseline-typed"
                      />
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          onClick={() => setQbStep("preview")}
                          disabled={qbApplying}
                          className="px-4 py-2 text-gray-300 hover:text-white text-sm transition disabled:opacity-40"
                          data-testid="sla-quiet-baseline-back"
                        >
                          Indietro
                        </button>
                        <button
                          onClick={applyQuietBaseline}
                          disabled={qbTyped !== "SILENZIA" || qbApplying}
                          className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition"
                          data-testid="sla-quiet-baseline-apply"
                        >
                          {qbApplying ? (
                            <Loader2 size={13} className="animate-spin" />
                          ) : (
                            <VolumeX size={13} />
                          )}
                          Silenzia {qbPreview.count}{" "}
                          {qbPreview.count === 1 ? "richiesta" : "richieste"}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SlaHealthWidget;
